import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    adminStats, adminListLeads, adminPatchLead, adminDeleteLead,
    adminListSimulations, adminListTelemetry, adminExportLeadsUrl,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Search, Download, LogOut, Trash2, Check, X, RefreshCw } from "lucide-react";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "leads", label: "Leads" },
    { id: "simulations", label: "Simulations" },
    { id: "telemetry", label: "Telemetry" },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("overview");

    const doLogout = () => {
        logout();
        toast.success("Signal closed.");
        nav("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-void text-ghost grain" data-testid="admin-dashboard">
            <header className="sticky top-0 z-40 bg-black/70 backdrop-blur-2xl border-b border-border">
                <div className="px-4 sm:px-8 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="w-2 h-2 bg-signal animate-pulse-glow" />
                        <span className="font-display font-black tracking-tighter text-lg">APIB</span>
                        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-signal">// CONSOLE</span>
                        <Link to="/" className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.3em] text-muted hover:text-signal">← back to site</Link>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.3em] text-muted">root <span className="text-signal">·</span> {user?.email}</span>
                        <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-3 py-1.5 border border-border hover:border-signal text-muted hover:text-signal text-[10px] font-mono uppercase tracking-[0.2em]">
                            <LogOut size={12} /> Logout
                        </button>
                    </div>
                </div>
                <nav className="px-4 sm:px-8 flex border-t border-border">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-[0.2em] border-r border-border ${tab === t.id ? "bg-signal/10 text-signal" : "text-muted hover:text-ghost"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
            </header>

            <main className="px-4 sm:px-8 py-8">
                {tab === "overview" && <Overview />}
                {tab === "leads" && <Leads />}
                {tab === "simulations" && <Simulations />}
                {tab === "telemetry" && <Telemetry />}
            </main>
        </div>
    );
}

function Overview() {
    const [stats, setStats] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        try { setStats(await adminStats()); } catch {} finally { setRefreshing(false); }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    if (!stats) {
        return <div className="font-mono text-xs text-muted">› loading stats…</div>;
    }

    return (
        <div className="space-y-8" data-testid="admin-overview">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-3xl font-black tracking-tighter uppercase">Overview</h2>
                <button onClick={refresh} className="inline-flex items-center gap-2 px-3 py-1.5 border border-border hover:border-signal text-muted hover:text-signal text-[10px] font-mono uppercase tracking-[0.2em]">
                    <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 border border-border">
                <StatTile label="TOTAL LEADS" value={stats.total_leads} hot />
                <StatTile label="LEADS TODAY" value={stats.leads_today} />
                <StatTile label="CONTACTED" value={stats.contacted_leads} />
                <StatTile label="SIMS TODAY" value={stats.simulations_today} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card title="PERSONA BREAKDOWN">
                    {Object.keys(stats.persona_breakdown || {}).length === 0 && <div className="font-mono text-xs text-muted">No data yet.</div>}
                    <div className="space-y-2">
                        {Object.entries(stats.persona_breakdown || {}).map(([k, v]) => {
                            const total = Object.values(stats.persona_breakdown).reduce((s, n) => s + n, 0) || 1;
                            const pct = Math.round((v / total) * 100);
                            return (
                                <div key={k}>
                                    <div className="flex justify-between font-mono text-[11px] mb-1">
                                        <span className="uppercase tracking-[0.2em] text-ghost">{k}</span>
                                        <span className="text-muted">{v} <span className="text-signal">· {pct}%</span></span>
                                    </div>
                                    <div className="h-1 bg-border">
                                        <div className="h-full bg-signal" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
                <Card title="TOP OBJECTIVES">
                    {(stats.top_objectives || []).length === 0 && <div className="font-mono text-xs text-muted">No simulations yet.</div>}
                    <ul className="space-y-1">
                        {(stats.top_objectives || []).map((o, i) => (
                            <li key={i} className="font-mono text-xs flex justify-between gap-3 py-1 border-b border-border/60 last:border-0">
                                <span className="text-ghost truncate">{o.objective}</span>
                                <span className="text-signal">×{o.count}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            </div>
        </div>
    );
}

function StatTile({ label, value, hot }) {
    return (
        <div className="p-5 border-r border-border last:border-r-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted">{label}</div>
            <div className={`mt-2 font-display text-3xl font-black tracking-tighter ${hot ? "text-signal glow-amber" : "text-ghost"}`}>{value}</div>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="border border-border">
            <div className="px-4 py-2 border-b border-border bg-black/40 text-[10px] font-mono uppercase tracking-[0.3em] text-signal">{title}</div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function Leads() {
    const [data, setData] = useState({ total: 0, items: [] });
    const [q, setQ] = useState("");
    const [persona, setPersona] = useState("");
    const [contacted, setContacted] = useState("");
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const limit = 25;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit, skip: page * limit };
            if (q) params.q = q;
            if (persona) params.persona = persona;
            if (contacted !== "") params.contacted = contacted === "true";
            setData(await adminListLeads(params));
        } catch (e) {
            toast.error("Failed to load leads.");
        } finally { setLoading(false); }
    }, [q, persona, contacted, page]);

    useEffect(() => { load(); }, [load]);

    const toggleContacted = async (lead) => {
        try {
            await adminPatchLead(lead.id, { contacted: !lead.contacted });
            toast.success(lead.contacted ? "Marked as not contacted." : "Marked as contacted.");
            load();
        } catch { toast.error("Update failed."); }
    };

    const remove = async (lead) => {
        if (!window.confirm(`Delete lead ${lead.email}?`)) return;
        try {
            await adminDeleteLead(lead.id);
            toast.success("Lead removed.");
            load();
        } catch { toast.error("Delete failed."); }
    };

    const exportCsv = () => {
        const token = localStorage.getItem("apib_admin_token");
        fetch(adminExportLeadsUrl(), { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(b => {
                const url = URL.createObjectURL(b);
                const a = document.createElement("a");
                a.href = url; a.download = "apib_leads.csv"; a.click();
                URL.revokeObjectURL(url);
                toast.success("Export streamed.");
            }).catch(() => toast.error("Export failed."));
    };

    return (
        <div className="space-y-6" data-testid="admin-leads">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="font-display text-3xl font-black tracking-tighter uppercase">Leads <span className="text-signal">/ {data.total}</span></h2>
                <button onClick={exportCsv} data-testid="admin-export-csv" className="inline-flex items-center gap-2 px-3 py-1.5 bg-signal text-void text-[10px] font-mono uppercase tracking-[0.2em] hover:bg-signal-soft">
                    <Download size={12} /> Export CSV
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 border border-border p-3 bg-obsidian">
                <div className="sm:col-span-2 flex items-center gap-2 border-b border-border pb-2">
                    <Search size={12} className="text-muted" />
                    <input value={q} onChange={e => { setPage(0); setQ(e.target.value); }} placeholder="search email · name · phone · objective" data-testid="admin-search"
                        className="flex-1 bg-transparent outline-none font-mono text-sm text-ghost placeholder:text-muted" />
                </div>
                <select value={persona} onChange={e => { setPage(0); setPersona(e.target.value); }} data-testid="admin-filter-persona"
                    className="bg-transparent border border-border px-2 py-1.5 font-mono text-xs text-ghost outline-none">
                    <option value="">all personas</option>
                    <option value="developer">developer</option>
                    <option value="enterprise">enterprise</option>
                    <option value="investor">investor</option>
                    <option value="researcher">researcher</option>
                </select>
                <select value={contacted} onChange={e => { setPage(0); setContacted(e.target.value); }} data-testid="admin-filter-contacted"
                    className="bg-transparent border border-border px-2 py-1.5 font-mono text-xs text-ghost outline-none">
                    <option value="">any status</option>
                    <option value="false">uncontacted</option>
                    <option value="true">contacted</option>
                </select>
            </div>

            <div className="border border-border overflow-x-auto">
                <table className="w-full font-mono text-xs">
                    <thead className="bg-black/40">
                        <tr className="text-left uppercase tracking-[0.2em] text-[10px] text-muted">
                            <th className="px-3 py-2">When</th>
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Email</th>
                            <th className="px-3 py-2">Phone</th>
                            <th className="px-3 py-2">Persona</th>
                            <th className="px-3 py-2">Objective</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody data-testid="admin-leads-rows">
                        {data.items.map(l => (
                            <tr key={l.id} className="border-t border-border hover:bg-signal/5">
                                <td className="px-3 py-2 text-muted">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-3 py-2 text-ghost">{l.name || "—"}</td>
                                <td className="px-3 py-2 text-signal">{l.email}</td>
                                <td className="px-3 py-2 text-ghost">{l.phone || "—"}</td>
                                <td className="px-3 py-2 uppercase">{l.persona}</td>
                                <td className="px-3 py-2 text-muted max-w-[280px] truncate" title={l.objective || ""}>{l.objective || "—"}</td>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggleContacted(l)} data-testid={`admin-toggle-${l.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] uppercase tracking-[0.2em] ${l.contacted ? "border-signal text-signal" : "border-border text-muted"}`}>
                                        {l.contacted ? <Check size={11} /> : <X size={11} />} {l.contacted ? "yes" : "no"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(l)} data-testid={`admin-delete-${l.id}`} className="text-muted hover:text-red-400" title="Delete">
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!loading && data.items.length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-muted">No leads match.</td></tr>
                        )}
                        {loading && (
                            <tr><td colSpan={8} className="px-3 py-8 text-center text-signal animate-pulse">› loading…</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between font-mono text-[11px] text-muted">
                <span>showing {page * limit + 1}-{Math.min((page + 1) * limit, data.total)} of {data.total}</span>
                <div className="flex gap-2">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-1 border border-border hover:border-signal disabled:opacity-30">← prev</button>
                    <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= data.total} className="px-2 py-1 border border-border hover:border-signal disabled:opacity-30">next →</button>
                </div>
            </div>
        </div>
    );
}

function Simulations() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(null);
    useEffect(() => { adminListSimulations(30).then(d => setItems(d.items || [])).catch(() => {}); }, []);
    return (
        <div className="space-y-6" data-testid="admin-simulations">
            <h2 className="font-display text-3xl font-black tracking-tighter uppercase">Simulator Runs</h2>
            <div className="border border-border divide-y divide-border">
                {items.map((s, i) => (
                    <div key={i} className="hover:bg-signal/5">
                        <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-left px-4 py-3 flex items-center justify-between gap-4 font-mono text-xs">
                            <span className="text-muted w-32">{(s.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                            <span className="flex-1 text-ghost truncate">{s.objective}</span>
                            <span className={`text-[10px] uppercase tracking-[0.2em] px-1.5 py-0.5 border ${s.source === "llm" ? "border-signal text-signal" : "border-border text-muted"}`}>{s.source}</span>
                            <span className="text-muted text-[10px]">{(s.sub_agents || []).length} agents</span>
                        </button>
                        {open === i && (
                            <div className="px-4 pb-4 font-mono text-[11px] space-y-2">
                                <div><span className="text-muted">summary: </span><span className="text-ghost">{s.plan_summary}</span></div>
                                <div>
                                    <div className="text-muted">reasoning:</div>
                                    <ol className="pl-4 space-y-0.5">
                                        {(s.reasoning || []).map(r => (
                                            <li key={r.step}><span className="text-signal">[{r.layer}]</span> {r.thought} <span className="text-muted">→</span> <span className="text-signal">{r.action}</span></li>
                                        ))}
                                    </ol>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {items.length === 0 && <div className="p-8 text-center font-mono text-xs text-muted">No simulations yet.</div>}
            </div>
        </div>
    );
}

function Telemetry() {
    const [items, setItems] = useState([]);
    useEffect(() => { adminListTelemetry(150).then(d => setItems(d.items || [])).catch(() => {}); }, []);
    return (
        <div className="space-y-6" data-testid="admin-telemetry">
            <h2 className="font-display text-3xl font-black tracking-tighter uppercase">Telemetry</h2>
            <div className="border border-border bg-obsidian">
                <div className="font-mono text-[11px] max-h-[70vh] overflow-y-auto p-3">
                    {items.map((e, i) => (
                        <div key={i} className="flex gap-3 py-0.5">
                            <span className="text-muted w-32">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</span>
                            <span className="text-signal w-32 uppercase tracking-[0.2em]">{e.event}</span>
                            <span className="text-ghost flex-1 truncate">{JSON.stringify(e.meta || {})}</span>
                        </div>
                    ))}
                    {items.length === 0 && <div className="text-center text-muted py-8">No events yet.</div>}
                </div>
            </div>
        </div>
    );
}
